import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Mic, Sparkles, LineChart } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: Mic,
    title: "Graba tu consulta",
    description:
      "Presiona grabar al inicio. Antropia escucha en segundo plano mientras tú te concentras en tu cliente. Funciona presencial o en videollamada.",
  },
  {
    number: "02",
    icon: Sparkles,
    title: "La IA analiza todo",
    description:
      "La IA procesa la conversación y extrae los puntos clave: cambios en el plan, datos nutricionales, estado de ánimo y acciones a seguir.",
  },
  {
    number: "03",
    icon: LineChart,
    title: "Tu cliente ve su progreso",
    description:
      "Tu cliente recibe un link personal con su progreso visual: gráficas de evolución, puntos clave y fotos comparativas.",
  },
];

export const HowItWorksSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="como-funciona"
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
    >
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]" />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-block text-[#E05D3A] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Cómo Funciona
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6"
          >
            Tres pasos. <span className="text-gradient">Cero complicaciones.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto"
          >
            De la consulta desordenada a la documentación perfecta en minutos.
          </motion.p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line - desktop */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px -translate-y-1/2">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
              className="h-full bg-gradient-to-r from-transparent via-[#E05D3A]/30 to-transparent origin-left"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.15 }}
                className="relative"
              >
                <div className="relative z-10 flex flex-col items-center text-center p-8 rounded-2xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] hover:border-[#E05D3A]/30 transition-all duration-300 group">
                  {/* Step number */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#E05D3A] text-white text-sm font-bold rounded-full">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#E05D3A]/10 text-[#E05D3A] mb-6 mt-4 group-hover:scale-110 transition-transform duration-300">
                    <step.icon className="w-8 h-8" />
                  </div>

                  {/* Content */}
                  <h3 className="font-outfit text-xl font-medium text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Mobile connector */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center py-4">
                    <motion.div
                      initial={{ scaleY: 0 }}
                      animate={isInView ? { scaleY: 1 } : {}}
                      transition={{ duration: 0.3, delay: 0.5 + index * 0.15 }}
                      className="w-px h-8 bg-gradient-to-b from-[#E05D3A]/50 to-transparent origin-top"
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
